import { BORNES_BULLE, CANAUX, dureeEnMots, TEMPS_DE_J_AI } from '@nextilt/commun';
import type { Bulle, Canal, TempsDeJAi } from '../api/types';

/**
 * Ce qu'une bulle EST, en mots : sa durée, son « où », ses heures d'ouverture, sa difficulté.
 *
 * « Un nœud n'est pas un pavé, c'est une étape classée. Il porte son rang, son canal et sa
 * durée dans une ligne discrète sous son titre. C'est le même vocabulaire que la ligne de
 * propriétés de la fiche : **une seule grammaire pour les mêmes valeurs, jamais deux** »
 * (SPEC §5.4). La durée et le « où » d'une bulle ne se disent que par `proprietesEnMots`.
 */

/**
 * Les choix de « J'ai » (le temps qu'on a devant soi) et du filtre « Ici, tu peux faire ». La
 * durée d'une tâche, elle, se tape à la minute dans la fiche et se dit par `dureeEnMots`
 * (`@nextilt/commun`), au travers de `proprietesEnMots`.
 */
export const MOTS_DU_TEMPS: Record<TempsDeJAi, string> = {
  dix_min: '10 min',
  trente_min: '30 min',
  une_heure: '1 h',
  demi_journee: 'Demi-journée',
  journee: 'La journée',
};

export const MOTS_CANAL: Record<Canal, string> = {
  sans_bouger: 'Sans bouger',
  il_faut_y_aller: 'Il faut y aller',
};

export const AUX_HEURES_D_OUVERTURE = 'Aux heures d’ouverture';

/**
 * « Difficulté pour toi », en étoiles (chantier `flottaison`, décision d'Igor du 2026-10-01) :
 * 1 facile, 5 difficile. Plus de « Ça va / Bof / Pas très agréable ».
 */
export const DIFFICULTE = 'Difficulté pour toi';
/** Les crans de difficulté, de 1 à 5 : les bornes du serveur (`BORNES_BULLE`, `@nextilt/commun`). */
export const ETOILES_MAX = BORNES_BULLE.chargeMax;
export const CHARGES = Array.from(
  { length: BORNES_BULLE.chargeMax - BORNES_BULLE.chargeMin + 1 },
  (_, i) => BORNES_BULLE.chargeMin + i
);
/** Le nom d'une étoile, lu au clavier et au survol : « 3 étoiles sur 5 ». */
export function motDeLEtoile(cran: number): string {
  const sens = cran === BORNES_BULLE.chargeMin ? ', facile' : cran === ETOILES_MAX ? ', difficile' : '';
  return `${cran} ${cran > 1 ? 'étoiles' : 'étoile'} sur ${ETOILES_MAX}${sens}`;
}
/** La difficulté dans la ligne discrète sous un titre : autant d'étoiles pleines que de crans. */
export function etoilesEnMots(charge: number): string {
  return '★'.repeat(Math.max(0, Math.min(ETOILES_MAX, Math.round(charge))));
}

/**
 * Les crans du temps, prêts à être posés en pastilles : `[clé, mot]`, du plus court au plus
 * long, dans l'ordre de `TEMPS_DE_J_AI` (`@nextilt/commun`). L'ordre n'est écrit qu'une fois :
 * une liste de crans recopiée finit toujours par en perdre un en route (le filtre du téléphone
 * avait perdu « la journée »).
 */
export function cransDuTemps(): [TempsDeJAi, string][] {
  return TEMPS_DE_J_AI.map((cle) => [cle, MOTS_DU_TEMPS[cle]]);
}

/** Sans bouger, puis il faut y aller : `[clé, mot]`, dans l'ordre de `CANAUX` (`@nextilt/commun`). */
export function cransDeCanal(): [Canal, string][] {
  return CANAUX.map((cle) => [cle, MOTS_CANAL[cle]]);
}

/** Ce que `proprietesEnMots` sait dire : la durée, le lieu de travail, les heures d'ouverture. */
export type ProprieteDite = 'duree' | 'lieu' | 'heures';

/**
 * **La durée et le « où » d'une bulle, à un seul endroit** (chantier nettoyage-mots, ticket 04) :
 * les mots de ce qui est réglé, dans l'ordre demandé ; ce qui n'est pas réglé se tait. Chaque
 * écran garde son ordre, et y ajoute ce qui lui est propre (le projet, les étoiles). Une valeur
 * que l'app ne connaît pas se dit telle quelle.
 */
export function proprietesEnMots(
  bulle: Partial<Pick<Bulle, 'effortMin' | 'canal' | 'heuresOuvrables'>>,
  ordre: readonly ProprieteDite[]
): string[] {
  return ordre
    .map((propriete) => {
      switch (propriete) {
        case 'duree':
          return bulle.effortMin != null ? dureeEnMots(bulle.effortMin) : null;
        case 'lieu':
          return bulle.canal ? (MOTS_CANAL[bulle.canal] ?? bulle.canal) : null;
        case 'heures':
          return bulle.heuresOuvrables === true ? AUX_HEURES_D_OUVERTURE : null;
      }
    })
    .filter((m): m is string => Boolean(m));
}

/** La ligne discrète sous un titre : ce que la bulle EST, jamais où elle en est. */
export function ligneDeProprietes(bulle: Bulle): string[] {
  return [
    ...proprietesEnMots(bulle, ['lieu', 'duree', 'heures']),
    bulle.charge ? etoilesEnMots(bulle.charge) : null,
  ].filter((m): m is string => Boolean(m));
}
